<template>
  <div class="biz-platform-page">
    <!-- Hero Banner -->
    <section class="hero-banner">
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <h1 class="hero-title">业务中台</h1>
        <p class="hero-sub">OA/CRM/ERP/MES/SRM 五大业务系统，企业运营全流程数字化</p>
      </div>
    </section>

    <!-- Platform Cards -->
    <section class="platform-section">
      <div class="content-container">
        <div class="platform-grid">
          <div
            v-for="(platform, index) in platforms"
            :key="index"
            class="platform-card"
          >
            <div class="card-visual" :style="{ background: `linear-gradient(135deg, ${platform.bgGradient}, #fff)` }">
              <span class="card-tag">业务中台</span>
              <div class="card-icon-wrap" :style="{ boxShadow: `0 8px 24px ${platform.iconShadowColor}` }">
                <Icon :icon="platform.icon" :size="36" :color="platform.color" />
              </div>
            </div>
            <div class="card-body">
              <h3 class="card-title">{{ platform.name }}</h3>
              <p class="card-desc">{{ platform.desc }}</p>
              <div class="card-tags">
                <span
                  v-for="tag in platform.tags"
                  :key="tag"
                  class="feature-tag"
                  :style="{ background: platform.tagBg, color: platform.color }"
                >
                  {{ tag }}
                </span>
              </div>
              <div class="card-actions">
                <button class="btn-trial" @click="showTrialModal">免费试用</button>
                <button class="btn-contact" @click="showTrialModal">联系销售</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { Icon } from '@/components/Icon'
import { usePortalState } from '../usePortalState'

defineOptions({ name: 'BizPlatformPage' })

const { showTrialModal } = usePortalState()

interface PlatformInfo {
  name: string
  shortName: string
  icon: string
  color: string
  bgGradient: string
  iconShadowColor: string
  tagBg: string
  desc: string
  tags: string[]
}

const platforms: PlatformInfo[] = [
  {
    name: 'OA办公自动化',
    shortName: 'OA',
    icon: 'lucide:briefcase',
    color: '#0066FF',
    bgGradient: '#E8F0FE',
    iconShadowColor: 'rgba(0,102,255,.15)',
    tagBg: '#E8F0FE',
    desc: '覆盖企业日常办公全流程',
    tags: ['流程审批', '公文管理', '会议管理']
  },
  {
    name: 'CRM客户管理',
    shortName: 'CRM',
    icon: 'lucide:users',
    color: '#00B96B',
    bgGradient: '#E6F7EC',
    iconShadowColor: 'rgba(0,185,107,.15)',
    tagBg: '#E6F7EC',
    desc: '全链路客户关系管理',
    tags: ['客户管理', '线索管理', '商机管理']
  },
  {
    name: 'ERP资源计划',
    shortName: 'ERP',
    icon: 'lucide:box',
    color: '#FF9500',
    bgGradient: '#FFF4E5',
    iconShadowColor: 'rgba(255,149,0,.15)',
    tagBg: '#FFF4E5',
    desc: '八大模块企业资源计划',
    tags: ['采购管理', '库存管理', '财务管理']
  },
  {
    name: 'MES制造执行',
    shortName: 'MES',
    icon: 'lucide:cpu',
    color: '#7C3AED',
    bgGradient: '#F3E8FF',
    iconShadowColor: 'rgba(124,58,237,.15)',
    tagBg: '#F3E8FF',
    desc: '车间级制造执行系统',
    tags: ['生产计划', '工单管理', '质量管理']
  },
  {
    name: 'SRM供应商管理',
    shortName: 'SRM',
    icon: 'lucide:truck',
    color: '#0891B2',
    bgGradient: '#E0F2FE',
    iconShadowColor: 'rgba(8,145,178,.15)',
    tagBg: '#E0F2FE',
    desc: '供应商全生命周期管理',
    tags: ['供应商准入', '采购寻源', '合同管理']
  }
]
</script>

<style lang="scss" scoped>
.hero-banner {
  height: 280px;
  background: linear-gradient(135deg, #00B96B, #0066FF);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  .hero-overlay {
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1), transparent 70%);
  }

  .hero-content {
    position: relative;
    z-index: 2;
    text-align: center;
    color: #fff;
  }

  .hero-title {
    font-size: 36px;
    font-weight: 800;
    margin-bottom: 8px;
  }

  .hero-sub {
    font-size: 16px;
    opacity: 0.8;
  }
}

.content-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
}

.platform-section {
  padding: 64px 24px;
}

.platform-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.platform-card {
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  .card-visual {
    height: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
  }

  .card-tag {
    position: absolute;
    top: 12px;
    left: 12px;
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.8);
    color: #4B5563;
  }

  .card-icon-wrap {
    width: 72px;
    height: 72px;
    background: #fff;
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .card-body {
    padding: 20px;
  }

  .card-title {
    font-size: 17px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .card-desc {
    font-size: 13px;
    color: #6B7280;
    margin-bottom: 12px;
    line-height: 1.6;
  }

  .card-tags {
    margin-bottom: 16px;
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
  }

  .feature-tag {
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 4px;
  }

  .card-actions {
    display: flex;
    gap: 8px;
  }

  .btn-trial {
    height: 32px;
    font-size: 12px;
    padding: 0 14px;
    flex: 1;
    background: #0066FF;
    color: #fff;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;

    &:hover {
      background: #0044CC;
    }
  }

  .btn-contact {
    height: 32px;
    font-size: 12px;
    padding: 0 14px;
    flex: 1;
    background: #fff;
    color: #4B5563;
    border: 1px solid #E5E7EB;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    &:hover {
      border-color: #0066FF;
      color: #0066FF;
    }
  }
}

@media (max-width: 1024px) {
  .platform-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .platform-grid {
    grid-template-columns: 1fr;
  }

  .hero-banner {
    height: 200px;

    .hero-title {
      font-size: 28px;
    }
  }
}
</style>
